import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  FormControl,
  Grid,
  Typography,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import RequiredLabel from "../../shared/RequiredLabel";
import { isValidPhoneNumber } from "../../utils/validation";

export default function BasicInfoForm({ formData, onChange, isUser }) {
  // バリデーション表示の状態
  const [showValidation, setShowValidation] = useState(false);

  // イニシャル2つだけローカル状態で管理
  const [first, setFirst] = useState("");
  const [second, setSecond] = useState("");

  // 最新の onChange を保持
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // 初期値だけformDataからセット（編集用）
  useEffect(() => {
    if (!formData.name) return;

    const clean = formData.name.replace(/[・.．]/g, "");

    if (clean.length === 2) {
      setFirst(clean[0]);
      setSecond(clean[1]);
    } else {
      setFirst("");
      setSecond("");
    }
  }, [formData.name]);

  // イニシャル入力用
  const sanitizeInitial = (value) =>
    value
      .replace(/[^A-Za-z]/g, "")
      .slice(0, 1)
      .toUpperCase();

  // first/second が変わった時だけ親へ同期
  const updateInitials = (nextFirst, nextSecond) => {
    // 値が変更されたらバリデーションをオフに
    setShowValidation(false);

    setFirst(nextFirst);
    setSecond(nextSecond);

    onChangeRef.current({
      name_first: nextFirst,
      name_second: nextSecond,
      name: !nextFirst || !nextSecond ? "" : `${nextFirst}・${nextSecond}`,
      validationTriggered: false,
    });
  };

  // 親コンポーネントからのバリデーション表示トリガーを検知
  useEffect(() => {
    if (formData.validationTriggered) {
      setShowValidation(true);
      // バリデーションエラーが表示された時に画面上部へスクロール
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setShowValidation(false);
    }
  }, [formData.validationTriggered]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // 値が変更されたらバリデーションをオフに
    setShowValidation(false);
    onChangeRef.current({ [name]: value, validationTriggered: false });
  };

  // 電話番号入力は全角→半角変換＋数字以外削除してから保存
  const handlePhoneChange = (e) => {
    const value = e.target.value
      .replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 65248))
      .replace(/[^0-9]/g, "");

    setShowValidation(false);

    onChangeRef.current({
      phone_number: value,
      validationTriggered: false,
    });
  };

  const isCompanyUser = !!formData.company_user_id;

  // 必須項目のバリデーションチェック
  const nameError = showValidation && (!first || !second);
  const initialFormatError =
    showValidation &&
    first &&
    second &&
    (!/^[A-Z]$/.test(first) || !/^[A-Z]$/.test(second));
  const birthdayError =
    showValidation && (!formData.birthday || formData.birthday.trim() === "");
  const nationalityError =
    showValidation &&
    (!formData.nationality || formData.nationality.trim() === "");
  const lastNameError =
    isCompanyUser &&
    showValidation &&
    (!formData.last_name || formData.last_name.trim() === "");
  const firstNameError =
    isCompanyUser &&
    showValidation &&
    (!formData.first_name || formData.first_name.trim() === "");

  const phoneError = !!(
    showValidation &&
    isUser &&
    formData.phone_number &&
    !isValidPhoneNumber(formData.phone_number)
  );

  // バリデーションエラーメッセージの生成
  const validationErrors = [];
  if (nameError) validationErrors.push("イニシャルは入力必須です。");
  if (initialFormatError) {
    validationErrors.push(
      "イニシャルの形式が不正です。アルファベット(大文字)で再入力してください。",
    );
  }
  const initialError = !!(nameError || initialFormatError);

  if (birthdayError) validationErrors.push("生年月日は入力必須です。");
  if (phoneError)
    validationErrors.push("電話番号は10桁または11桁の数字で入力してください。");
  if (nationalityError) validationErrors.push("国籍は入力必須です。");
  if (lastNameError) validationErrors.push("姓は入力必須です。");
  if (firstNameError) validationErrors.push("名は入力必須です。");

  return (
    <Box>
      <Typography
        variant="h5"
        gutterBottom
        fontWeight="bold"
        sx={{ marginBottom: "24px" }}
      >
        基本情報
      </Typography>

      {/* バリデーションエラーメッセージの表示 */}
      {showValidation && validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography>イニシャル</Typography>
              <RequiredLabel show={true} />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                value={first}
                onChange={(e) =>
                  updateInitials(sanitizeInitial(e.target.value), second)
                }
                inputProps={{ maxLength: 1 }}
                error={initialError}
                sx={{ width: 56 }}
                placeholder="A"
              />
              <Typography>・</Typography>
              <TextField
                value={second}
                onChange={(e) =>
                  updateInitials(first, sanitizeInitial(e.target.value))
                }
                inputProps={{ maxLength: 1 }}
                error={initialError}
                sx={{ width: 56 }}
                placeholder="A"
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              例：A・A（各1文字ずつ）
            </Typography>
          </Box>
        </Grid>

        {/* ユーザーの場合のみ、電話番号を表示 */}
        {isUser && (
          <Grid item xs={12} sm={6}>
            <Box
              sx={{ display: "flex", flexDirection: "column", width: "100%" }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Typography>電話番号</Typography>
              </Box>
              <TextField
                fullWidth
                id="phone_number"
                name="phone_number"
                value={formData.phone_number || ""}
                onChange={handlePhoneChange}
                placeholder="09012345678"
                autoComplete="tel"
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                variant="outlined"
                error={phoneError}
              />
            </Box>
          </Grid>
        )}

        {/* 従業員の場合、表示 */}
        {isCompanyUser && (
          <Grid item xs={12}>
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Typography>姓</Typography>
                    <RequiredLabel show={true} />
                  </Box>
                  <TextField
                    required
                    fullWidth
                    name="last_name"
                    placeholder="山田"
                    value={formData.last_name || ""}
                    onChange={handleChange}
                    error={lastNameError}
                  />
                </Grid>

                <Grid item xs={6}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Typography>名</Typography>
                    <RequiredLabel show={true} />
                  </Box>
                  <TextField
                    required
                    fullWidth
                    name="first_name"
                    placeholder="太郎"
                    value={formData.first_name || ""}
                    onChange={handleChange}
                    error={firstNameError}
                  />
                </Grid>
              </Grid>

              <Typography variant="caption" color="text.secondary">
                フルネームを入力してください
              </Typography>
            </Box>
          </Grid>
        )}

        <Grid item xs={12}>
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography>生年月日</Typography>
              <RequiredLabel show={true} />
            </Box>
            <TextField
              required
              fullWidth
              id="birthday"
              name="birthday"
              type="date"
              value={formData.birthday || ""}
              onChange={handleChange}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                max: new Date().toISOString().split("T")[0],
              }}
              error={birthdayError}
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Typography sx={{ mb: 1 }}>性別</Typography>
            <FormControl>
              <RadioGroup
                row
                id="gender"
                name="gender"
                value={formData.gender || ""}
                onChange={handleChange}
              >
                <FormControlLabel
                  value="male"
                  control={<Radio />}
                  label="男性"
                />
                <FormControlLabel
                  value="female"
                  control={<Radio />}
                  label="女性"
                />
                <FormControlLabel
                  value="other"
                  control={<Radio />}
                  label="その他"
                />
              </RadioGroup>
            </FormControl>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography>国籍</Typography>
              <RequiredLabel show={true} />
            </Box>
            <TextField
              required
              fullWidth
              id="nationality"
              name="nationality"
              value={formData.nationality || ""}
              onChange={handleChange}
              placeholder="日本"
              variant="outlined"
              error={nationalityError}
            />
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Typography sx={{ mb: 1 }}>最寄り駅（路線）</Typography>
            <TextField
              fullWidth
              id="nearest_station_line"
              name="nearest_station_line"
              value={formData.nearest_station_line || ""}
              onChange={handleChange}
              placeholder="JR"
              variant="outlined"
            />
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Typography sx={{ mb: 1 }}>最寄り駅（駅名）</Typography>
            <TextField
              fullWidth
              id="nearest_station"
              name="nearest_station"
              value={formData.nearest_station || ""}
              onChange={handleChange}
              placeholder="東京"
              variant="outlined"
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Typography sx={{ mb: 1 }}>最終学歴</Typography>
            <TextField
              fullWidth
              id="final_academic_background"
              name="final_academic_background"
              value={formData.final_academic_background || ""}
              onChange={handleChange}
              placeholder="○○大学 ○○学部 ○○学科"
              variant="outlined"
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
